import { useEffect, useRef, useState, useCallback } from "react";
import { updateCategoriesForm } from "../../lib/actions";
import { useNavigate, useParams } from "react-router-dom";
import { FiUploadCloud, FiLink } from "react-icons/fi";
import { MdOutlineDeleteOutline, MdAddCircleOutline, MdLayers } from "react-icons/md";
import DropDownFilterPaginations from "../../components/GDropDownList/DropDownFilterPaginations";
import { ThreeDot } from "react-loading-indicators";
import { useTranslation } from "react-i18next";
import { BiSortAlt2 } from "react-icons/bi";

// --- Sub Component: Input Configurator ---
const InputConfigurator = ({ 
    input, 
    config, 
    updateConfig, 
    currentLang, 
    allConfigs, 
    inputsData,
    generateNewOptionId 
}) => {
    
    // ... (Keep existing Toggle & Handler functions: handleFilteringToggle, etc.) ...
    const handleFilteringToggle = () => {
        const newValue = config.is_filtering === 1 ? 0 : 1;
        updateConfig(input.id, 'is_filtering', newValue);
    };

    const handleTypeFilteringChange = (e) => {
        updateConfig(input.id, 'type_filtering', e.target.value);
    };

    const handlePriorityChange = (e) => {
        updateConfig(input.id, 'priority', e.target.value);
    };

    const handleParentChange = (e) => {
        updateConfig(input.id, 'parent_id', e.target.value);
    };
    
    // ... (Keep existing Helpers: getInputLabel, availableParents, selectedParentConfig) ...
    const getInputLabel = (id) => {
        const inp = inputsData.find(i => i.id === Number(id));
        return inp ? (inp.label?.[currentLang] || inp.label?.en) : 'Unknown';
    };

    const availableParents = allConfigs.filter(c => c.id !== input.id);
    const selectedParentConfig = config.parent_id 
        ? allConfigs.find(c => c.id.toString() === config.parent_id.toString()) 
        : null;

    // ... (Keep existing getFilterTypeOptions) ...
    const getFilterTypeOptions = () => {
        const inputType = input.type || 'text';
        switch (inputType) {
            case 'number':
            case 'integer':
            case 'float':
                return [
                    { value: 'value', label: currentLang === "en" ? 'Value (Exact Match)' : 'قيمة (مطابقة تامة)' }, 
                    { value: 'range', label: currentLang === "en" ? 'Range (Min/Max)' : 'نطاق (الأدنى/الأعلى)' }
                ];
            case 'select':
            case 'multi_select':
            case 'radio':
            case 'checkbox':
                return [
                    { value: 'select', label: currentLang === "en" ? 'Choose (Single)' : 'اختيار (واحد)' }, 
                    { value: 'multi_select', label: currentLang === "en" ? 'Choose (Multiple)' : 'اختيار (متعدد)' }
                ];
            case 'text':
            case 'textarea':
                return [{ value: 'text_search', label: currentLang === "en" ? 'Text Search' : 'بحث نصي' }];
            default:
                return [];
        }
    };

    const addOption = () => {
         const tempId = generateNewOptionId(); 
         const newOptions = [...(config.options || []), { 
             id: tempId, 
             en: '', 
             ar: '', 
             priority: 0, 
             relation_id: '' 
        }];
         updateConfig(input.id, 'options', newOptions);
    };

    const updateOption = (index, key, value) => {
        const newOptions = [...(config.options || [])];
        if (!newOptions[index]) return;
        newOptions[index] = { ...newOptions[index], [key]: value };
        updateConfig(input.id, 'options', newOptions);
    }

     const removeOption = (index) => {
        const newOptions = (config.options || []).filter((_, i) => i !== index);
        updateConfig(input.id, 'options', newOptions);
    }

    const inputType = input.type || 'text';
    const isSelectType = ['select', 'multi_select', 'radio', 'checkbox'].includes(inputType);
    const filterOptions = getFilterTypeOptions();

    return (
        <div className="p-4 bg-gray-800/50 rounded-lg mt-4 space-y-5 border border-gray-700 transition-all duration-300 relative">
            
            {/* Priority & Parent Select */}
            <div className="flex gap-4 mb-4">
                 <div className="flex-1">
                    <label className="text-xs text-gray-400 block mb-1">{currentLang === "en" ? "Input Priority" : "أولوية الحقل"}</label>
                    <div className="flex items-center bg-thirdly/50 rounded-lg px-2 border border-gray-600">
                        <BiSortAlt2 className="text-secondary"/>
                        <input 
                            type="number" 
                            value={config.priority || 0} 
                            onChange={handlePriorityChange}
                            className="w-full bg-transparent border-none text-white focus:ring-0 p-2"
                            placeholder="0"
                        />
                    </div>
                 </div>

                 <div className="flex-1">
                    <label className="text-xs text-gray-400 block mb-1">{currentLang === "en" ? "Parent Input" : "الحقل الأصلي"}</label>
                    <div className="flex items-center bg-thirdly/50 rounded-lg px-2 border border-gray-600">
                        <FiLink className="text-blue-400"/>
                        <select 
                            value={config.parent_id || ''} 
                            onChange={handleParentChange}
                            className="w-full bg-transparent border-none text-white focus:ring-0 p-1"
                        >
                            <option value="">{currentLang === "en" ? "No Parent" : "بدون أصل"}</option>
                            {availableParents.map(parent => (
                                <option key={parent.id} value={parent.id}>
                                    {getInputLabel(parent.id)}
                                </option>
                            ))}
                        </select>
                    </div>
                 </div>
            </div>

            <hr className="border-gray-700" />
            
            {/* Filtering Settings */}
            <h4 className="text-white font-semibold text-sm">{currentLang === "en" ? "Filtering Configuration" : "إعدادات التصفية"}</h4>
            
            {filterOptions.length > 0 ? (
                 <div className="flex items-center justify-between p-3 bg-thirdly/50 rounded-lg">
                    <label className="text-white text-sm">{currentLang === "en" ? "Enable Filtering" : "تفعيل التصفية"}</label>
                    <button 
                        type="button" 
                        onClick={handleFilteringToggle} 
                        className={`relative w-10 h-5 rounded-full transition-colors ${config.is_filtering === 1 ? 'bg-green-600' : 'bg-gray-500'}`}
                    >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-200 ${config.is_filtering === 1 ? (currentLang === 'ar' ? 'left-0.5' : 'left-5.5') : (currentLang === 'ar' ? 'left-5.5' : 'left-0.5')}`} />
                    </button>
                </div>
            ) : (
                <p className="text-gray-400 text-sm p-3">{currentLang === "en" ? "Filtering not applicable." : "التصفية غير متاحة."}</p>
            )}

            {config.is_filtering === 1 && filterOptions.length > 0 && (
                <div className="space-y-2 p-3 bg-thirdly/50 rounded-lg">
                    <label className="block text-white text-sm">{currentLang === "en" ? "Filter Interface Type" : "واجهة نوع التصفية"}</label>
                    <select
                        value={config.type_filtering || ''}
                        onChange={handleTypeFilteringChange}
                        className="w-full px-2 py-1 bg-furthly border border-gray-500/100 text-white rounded-lg text-sm"
                    >
                        <option value="">{currentLang === "en" ? "Select Type" : "اختر النوع"}</option>
                        {filterOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Options Management */}
            {isSelectType && (
                 <div className="space-y-3 pt-5 border-t border-gray-600">
                    <div className="flex justify-between items-center">
                        <label className="block text-white font-semibold">{currentLang === "en" ? "Options" : "الخيارات"}</label>
                        <button type="button" onClick={addOption} className="text-secondary text-sm hover:text-white transition flex items-center gap-1 bg-thirdly px-3 py-1 rounded-lg hover:bg-secondary/80">
                            <MdAddCircleOutline className="w-4 h-4"/> {currentLang === "en" ? "Add Option" : "إضافة خيار"}
                        </button>
                    </div>
                    
                    {config.parent_id && !selectedParentConfig?.options?.length && (
                        <div className="text-yellow-500 text-xs bg-yellow-500/10 p-2 rounded">
                            {currentLang === "en" ? "Warning: Parent has no options to link." : "تحذير: لا توجد خيارات في الأصل للربط."}
                        </div>
                    )}

                    <div className="space-y-3 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                        {(config.options || []).map((option, index) => (
                            <div key={index} className="flex flex-col gap-2 bg-black/20 p-3 rounded-lg border border-gray-700">
                                
                                <div className="flex gap-2 py-2">
                                    {/* --- FIX IS HERE: ADDED 'required' --- */}
                                    <input
                                        type="text"
                                        placeholder="EN Name"
                                        required 
                                        value={option.en || ''}
                                        onChange={(e) => updateOption(index, 'en', e.target.value)}
                                        className="flex-1 px-1.5 py-2 bg-thirdly border border-gray-500/50 text-white rounded text-sm"
                                    />
                                    <input
                                        type="text"
                                        placeholder="AR Name"
                                        required 
                                        value={option.ar || ''}
                                        onChange={(e) => updateOption(index, 'ar', e.target.value)}
                                        className="flex-1 px-1.5 py-2 bg-thirdly border border-gray-500/50 text-white rounded text-sm"
                                    />
                                    
                                    <div className="w-20" title="Option Priority">
                                         <input
                                            type="number"
                                            value={option.priority || 0}
                                            onChange={(e) => updateOption(index, 'priority', e.target.value)}
                                            className="w-full py-2 bg-thirdly border border-gray-500/50 text-white rounded text-center text-sm"
                                        />
                                    </div>

                                    <button type="button" onClick={() => removeOption(index)} className="text-red-500 hover:text-red-700 transition-colors">
                                        <MdOutlineDeleteOutline className="w-5 h-5" />
                                    </button>
                                </div>

                                {config.parent_id && selectedParentConfig && (
                                    <div className="flex items-center gap-2 mt-1">
                                        <FiLink className="text-gray-400 w-4 h-4" />
                                        <select
                                            value={option.relation_id || ''} 
                                            onChange={(e) => updateOption(index, 'relation_id', e.target.value)}
                                            className="flex-1 px-2 py-1 mb-2 bg-thirdly border border-gray-500/50 text-gray-300 rounded text-xs"
                                        >
                                            <option value="">
                                                {currentLang === "en" 
                                                    ? `Select Parent ${getInputLabel(config.parent_id)} Option` 
                                                    : `اختر خيار ${getInputLabel(config.parent_id)} المرتبط`}
                                            </option>
                                            
                                            {selectedParentConfig.options && selectedParentConfig.options.map((parentOpt, pIdx) => (
                                                <option key={pIdx} value={parentOpt.id}> 
                                                    {currentLang === 'en' ? parentOpt.en : parentOpt.ar} 
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};


export default function EditCategoiresUi({ categoriesData, categoryByIdData, inputsData, search }) {

    const nav = useNavigate();
    const lastSearchRef = useRef(null);
    const [check, setCheck] = useState(null);
    const {categoryId} = useParams();
    const [categoryIdData, setCategoryIdData] = useState(null)
    const [categoryPriority, setCategoryPriority] = useState(0); 
    const [inputConfigurations, setInputConfigurations] = useState([]);
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);
    
    // Ref for generating negative IDs: -1, -2, -3...
    const negativeIdCounter = useRef(0);

    const { i18n } = useTranslation();
    const t = i18n.t.bind(i18n); 
    const currentLang = i18n.language;

    // Helper to generate IDs
    const generateNewOptionId = useCallback(() => {
        negativeIdCounter.current = negativeIdCounter.current - 1;
        return negativeIdCounter.current;
    }, []);

    useEffect(() => {
        if (!categoryByIdData || Object.keys(categoryByIdData).length === 0) return;

        // Basic Info
        setCheck(categoryByIdData?.active);
        setImage(categoryByIdData?.logo);
        setCategoryIdData(categoryByIdData?.category_parent_id);
        setCategoryPriority(categoryByIdData?.priority || 0); 
        
        // 3. MAP EXISTING DATA - PRESERVE IDs from API
        const initialConfigs = (categoryByIdData.inputs || []).map(input => ({
            id: input.id,
            is_filtering: input.is_filtering ? 1 : 0,
            type_filtering: input.type_filtering || null,
            priority: input.priority || 0, 
            parent_id: input.parent_id || null, 
            options: input.options ? input.options.map(opt => ({ 
                id: opt.id, // e.g. 1, 8, 12. Kept exactly as is.
                en: opt.en, 
                ar: opt.ar,
                priority: opt.priority || 0,
                relation_id: opt.relation_id || null 
            })) : null
        }));
        setInputConfigurations(initialConfigs);

        // Search text handling
        const lang = currentLang;
        const nameObj = categoryByIdData.category_parent_name;
        const text = nameObj ? (lang === "en" ? nameObj.en : nameObj.ar) : "";

        if (text && search && lastSearchRef.current !== text) {
            lastSearchRef.current = text; 
            search(text);
        }

    }, [categoryByIdData, currentLang, search]);

    const onchange = (e) => {
        setCategoryIdData(e);
    };

    const handleChangeCheck = () => {
        setCheck(!check);
    };

    const toggleInputEnabled = (id) => {  
        setInputConfigurations(prev => {
            const exists = prev.some(item => item.id === id);
            if (exists) {
                return prev.filter(item => item.id !== id);
            } else {
                const inputDetails = inputsData.find(i => i.id === id);
                const inputType = inputDetails ? inputDetails.type : 'text'; 
                const needsOptions = ['select', 'multi_select', 'radio', 'checkbox'].includes(inputType);

                return [...prev, {
                    id: id,
                    is_filtering: 0, 
                    type_filtering: null,
                    priority: 0,
                    parent_id: null,
                    options: needsOptions ? [] : null 
                }];
            }
        });
    };  

    const updateInputConfig = (id, key, value) => {
        setInputConfigurations(prev =>
            prev.map(item =>
                item.id === id ? { ...item, [key]: value } : item
            )
        );
    };

    const handleSubmit = async (e) => {
        setLoading(true);
        e.preventDefault();
        const formData = new FormData(e.target);
        
        formData.append('priority', categoryPriority);

        inputConfigurations.forEach((config, key) => {
            formData.append(`inputs[${key}][id]`, config.id);
            formData.append(`inputs[${key}][priority]`, config.priority || 0); 
            formData.append(`inputs[${key}][is_filtering]`, config.is_filtering === 1 ? 1 : 0);

            if (config.parent_id) {
                formData.append(`inputs[${key}][parent_id]`, config.parent_id);
            }
            
            if (config.type_filtering) {
                formData.append(`inputs[${key}][type_filtering]`, config.type_filtering);
            }

            if (config.options && config.options.length > 0) {
                // Filter out empty options to avoid "field is required" error
                // We keep options that have at least an English OR Arabic name, 
                // but the UI 'required' attribute should force both.
                
                let validOptionIndex = 0; // Use a separate index for the form data to stay sequential

                config.options.forEach((option) => {
                    // Check if at least one name is present to consider it a valid attempt
                    if (option.en || option.ar) {
                        
                        formData.append(`inputs[${key}][options][${validOptionIndex}][id]`, option.id);
                        formData.append(`inputs[${key}][options][${validOptionIndex}][en]`, option.en || '');
                        formData.append(`inputs[${key}][options][${validOptionIndex}][ar]`, option.ar || '');
                        formData.append(`inputs[${key}][options][${validOptionIndex}][priority]`, option.priority || 0);

                        if (option.relation_id) {
                            formData.append(`inputs[${key}][options][${validOptionIndex}][relation_id]`, option.relation_id);
                        }

                        validOptionIndex++;
                    }
                });
            }
        });

        await updateCategoriesForm(categoryId, formData, navigate);
        setLoading(false);
    };
    
    const navigate = (route) => {
        nav(route);
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImage(URL.createObjectURL(file));
        }
    };

    const handleDeleteImage = () => {
        setImage(null);
    };


    if ((!categoryByIdData || Object.entries(categoryByIdData).length < 1)) {
        return <div className="flex justify-center items-center h-[25rem]"><ThreeDot variant="bounce" color="#D06421" size="large" text="" textColor="" /></div> 
    };

    const nameAr = categoryByIdData?.name?.ar || '';
    const nameEn = categoryByIdData?.name?.en || '';
    const descriptionAr = categoryByIdData?.description?.ar || '';
    const descriptionEn = categoryByIdData?.description?.en || '';

    return (
        <form onSubmit={handleSubmit} className="pb-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Category Parent" : "الفئة الأب"}</label>
                    <DropDownFilterPaginations search={search} options={categoriesData} value={categoryIdData} onchange={onchange} defaultText={currentLang === "en" ? "Select parent (optional)" : "أختر الفئة (اختياري) "} paramkey="category_id" />
                </div>

                <div className="">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Category Priority" : "أولوية الفئة"}</label>
                    <input
                        type="number"
                        value={categoryPriority}
                        onChange={(e) => setCategoryPriority(e.target.value)}
                        className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                        placeholder="0"
                    />
                </div>

                <div className="">
                    <label className="block text-white mb-2"> {currentLang === "en" ? "Name (AR)" : "الاسم (عربي)"} </label>
                    <input
                        type="text"
                        defaultValue={nameAr}
                        name="name_ar"
                        className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                        required
                    />
                </div>
                <div className="">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Name (EN)" : "الاسم (إنجليزي)"}</label>
                    <input
                        type="text"
                        defaultValue={nameEn}
                        name="name_en"
                        className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                        required
                    />
                </div>
                <div className="">
                    <label className="block text-white mb-2"> {currentLang === "en" ? "Description (AR)" : "الوصف (عربي)"} </label>
                    <input
                        type="text"
                        defaultValue={descriptionAr}
                        name="description_ar"
                        className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                    />
                </div>
                <div className="">
                    <label className="block text-white mb-2">{currentLang === "en" ? "Description (EN)" : "الوصف (إنجليزي)"} </label>
                    <input
                        type="text"
                        defaultValue={descriptionEn}
                        name="description_en"
                        className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                    />
                </div>
                <div className="flex gap-4 sm:flex-row flex-col sm:justify-between items-start mt-2">
                    <div className="flex flex-row justify-center items-center gap-6">
                        <div className="flex items-center gap-3">
                            <div
                                onClick={handleChangeCheck}
                                className={`relative w-14 h-6 rounded-full cursor-pointer transition
                                ${check ? (currentLang === 'ar' ? 'bg-secondary' : 'bg-secondary') : (currentLang === 'ar' ? 'bg-gray-400' : 'bg-gray-400')}`}
                            >
                                <span
                                className={`w-5 h-4 mt-1 bg-white rounded-full shadow-md block
                                    transform transition
                                    ${check ? (currentLang === 'ar' ? '-translate-x-1' : 'translate-x-7') : (currentLang === 'ar' ? '-translate-x-7' : 'translate-x-1')}`}
                                />
                            </div>
                            <label className="text-white">
                            {check
                                ? currentLang === "en"
                                ? "active"
                                : "فعال"
                                : currentLang === "en"
                                ? "unactive"
                                : "غير فعال"
                            }
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            
            {!categoryIdData &&
                <div className="mb-6">
                        <label className="block text-white mb-2">{currentLang === "en" ? "Photo" : "الصورة"}</label>
                        <div className="bg-thirdly p-6 rounded-2xl border-2 border-dashed border-gray-500/50 hover:border-secondary transition-colors">
                            <div className="relative h-40 flex justify-center items-center">
                                {!image ? (
                                    <div className="text-center">
                                        <FiUploadCloud className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                        <span className="text-sm text-gray-300">
                                            {currentLang === "en" ? "Click to upload" : "انقر للرفع"}
                                        </span>
                                        <input type="file" name="logo" accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={handleImageChange}  />
                                    </div>
                                ) : (
                                    <div className="relative h-full">
                                        <img src={image} alt="Preview" className="h-full object-contain rounded-lg" />
                                        <button type="button" className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-lg" onClick={handleDeleteImage}>
                                            <MdOutlineDeleteOutline className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                </div>
            }

            {categoryIdData &&
                <div className="mt-8 border-t border-gray-700 pt-6">
                    <div className="flex items-center gap-2 mb-6">
                        <MdLayers className="text-secondary w-6 h-6"/>
                        <h3 className="text-white text-xl font-semibold">{currentLang === "en" ? "Configure Inputs & Dependencies" : "تكوين الحقول والتبعيات"} </h3>
                    </div>
                    
                    <div className="bg-primary/20 rounded-xl p-4 border border-gray-700">
                        {(inputsData || []).length > 0 ? (
                            // 5. GRID LAYOUT APPLIED HERE
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {inputsData.map((item) => {
                                    const config = inputConfigurations.find(c => c.id === item.id);
                                    const isEnabled = !!config;
                                    const inputType = item.type || 'text'; 

                                    return (
                                        <div key={item.id} className={`p-4 rounded-xl border transition-all duration-300 ${isEnabled ? 'bg-gray-800/80 border-secondary/50' : 'bg-transparent border-gray-700'}`}>
                                            
                                            <div className="flex items-center justify-between gap-4 mb-2">
                                                <div className="flex flex-col">
                                                    <span className={`font-medium text-lg ${isEnabled ? 'text-white' : 'text-gray-400'}`}>
                                                        {item.label?.[currentLang] || item.label?.en}
                                                    </span>
                                                    <span className="text-xs text-gray-500 uppercase tracking-wider">{inputType}</span>
                                                </div>

                                                <div
                                                    onClick={() => toggleInputEnabled(item.id)}
                                                    className={`relative w-12 h-6 rounded-full cursor-pointer transition-colors duration-200
                                                    ${isEnabled ? 'bg-secondary' : 'bg-gray-600'}`}
                                                >
                                                    <span
                                                    className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200
                                                        ${isEnabled ? (currentLang === 'ar' ? 'left-1' : 'left-7') : (currentLang === 'ar' ? 'left-7' : 'left-1')}`}
                                                    />
                                                </div>
                                            </div>
                                            
                                            {isEnabled && (
                                                <InputConfigurator
                                                    input={item} 
                                                    config={config}
                                                    updateConfig={updateInputConfig}
                                                    currentLang={currentLang}
                                                    allConfigs={inputConfigurations}
                                                    inputsData={inputsData}
                                                    generateNewOptionId={generateNewOptionId}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-white text-center py-10">{currentLang === "en" ? "No inputs available." : "لا توجد حقول متاحة."}</p>
                        )}
                    </div>
                </div>
            }

            <div className="flex justify-center w-full mt-8">
                <button type="submit" disabled={loading} className={`w-full md:w-1/3 bg-secondary text-white py-3 rounded-xl font-semibold shadow-lg transition hover:bg-orange-600 ${loading ? "cursor-not-allowed opacity-70" : "" }`} >
                    {loading ? (
                        <div className="flex items-center justify-center gap-2">
                             <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                            </svg>
                            {currentLang === "en" ? "Saving..." : "جاري الحفظ..."}
                        </div>
                    ) : (
                        t("button.save")
                    )}
                </button>
            </div>
        </form>
    );
};